import { Directive, Input, ViewChild } from '@angular/core';
import {
  DynamicTypeaheadBaseComponent,
  DynamicTypeaheadEmployeeBaseComponent,
} from '@ups/xplat/dynamic-render';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import {
  bindTypeaheadViewToControl,
  passMultiSelectValueChange,
  typeaheadPickOrNull,
  TypeaheadViewStatus,
  typeaheadViewControl,
  typeaheadViewItem,
  typeaheadViewStatus,
} from './dynamic-typeahead-view-binding';

type SelectedItem = Parameters<
  DynamicTypeaheadBaseComponent['valueChangeItem']
>[0];

@Directive()
export abstract class DynamicTypeaheadEmployeeWebBaseComponent extends DynamicTypeaheadEmployeeBaseComponent {
  @ViewChild('dropdown') dropdown: DropDownListComponent;
  /** Tabindex to propagate to the underlying kendo dropdown */
  @Input({ transform: parseInt }) tabindex: number;

  setValue(value: unknown) {
    if (this.dropdown) {
      this.dropdown.value = value;
    }
  }

  /** A kendo-multiselect's (valueChange) hands its whole selection (see passMultiSelectValueChange). */
  multiSelectValueChange(items: SelectedItem[]) {
    passMultiSelectValueChange(this, items);
  }

  /** Task 35874: the object-only value for a `[value]`-bound kendo control. */
  get viewItem(): SelectedItem | null {
    return typeaheadViewItem(this.selectedItem);
  }

  /** The control's ng-* status, mirrored onto a `[value]`-bound kendo control (PR 5721 review). */
  get viewStatus(): TypeaheadViewStatus {
    return typeaheadViewStatus(this.viewControl());
  }

  /** The form control's disabled state, for `[disabled]` on a kendo control bound by `[value]`. */
  get viewDisabled(): boolean {
    return !!this.viewControl()?.disabled;
  }

  /** A user pick writes the form control (as `[formControlName]` did), then the shared selection logic runs. */
  pickFromView(item: SelectedItem) {
    const pick = typeaheadPickOrNull(item, this.config?.placeholder);
    this.updateValue(pick);
    this.valueChangeItem(pick);
  }

  /** Marks the form control touched on blur (as `[formControlName]` did), so required errors show. */
  blurFromView() {
    this.viewControl()?.markAsTouched();
    this.onBlur();
  }

  /** Repaint on every control write; call once from ngOnInit, after super.ngOnInit(). */
  protected bindViewToControl() {
    bindTypeaheadViewToControl(
      this.viewControl(),
      this.destroy$,
      () => this.resetSelectedItems(),
      () => this.cdr?.markForCheck()
    );
  }

  private viewControl() {
    return typeaheadViewControl(this.config, this.group);
  }
}
